import type { ContentHref, ContentRelationKey } from '@generalTypes/apiTypes';
import type { ContentRelationsMap } from '@generalTypes/generalTypes';
import type { EducationalActivityType } from '@generalTypes/samTypes';
import {
  selectApiWithPendingChangesRelationsToAndFromMap,
  selectRawApiRelationsToAndFromMap,
} from '@newStore/documentApi/documentApiSelectors';
import type { RelationsToAndFromMap } from '@newStore/documentApi/documentApiTypes';
import { getDiffForArraysOfStrings } from '@newStore/documentUI/transformProposal/asideDiffText';
import { selectEducationalActivityTypes } from '@newStore/externalData/externalDataSelectors';
import { createTypedSelector } from '@newStore/genericHelpers';
import type {
  AsideChangeMessageSelector,
  EditAsideFoundationalSuggestions,
} from '@nodeTypeConfig/configTypes';

const getFoundationalSuggestions = (
  activities: (EducationalActivityType & {
    isChecked?: boolean;
    relationKey?: ContentRelationKey | undefined;
  })[],
  relations: RelationsToAndFromMap,
  href: ContentHref,
  config: EditAsideFoundationalSuggestions,
) => {
  const suggestedEducationalActivities = relations.from[href]?.filter(
    (relation) => config.options.relationType === relation.relationtype,
  );
  return activities?.map((activity) => {
    const relation = suggestedEducationalActivities?.find(
      (rel) => rel.to.href === activity.$$meta.permalink,
    );
    return { ...activity, isChecked: !!relation, relationKey: relation?.key };
  });
};

export const selectFoundationalSuggestions = createTypedSelector(
  [
    (state) => selectEducationalActivityTypes(state),
    (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
    (state, href: ContentHref, config: EditAsideFoundationalSuggestions) => ({
      href,
      config,
    }),
  ],
  (activities: EducationalActivityType[], relations: ContentRelationsMap, { href, config }) => {
    return getFoundationalSuggestions(activities, relations, href, config);
  },
);

export const selectChangeMessageForFoundationalSuggestions: AsideChangeMessageSelector<EditAsideFoundationalSuggestions> =
  createTypedSelector(
    [
      (state) => selectApiWithPendingChangesRelationsToAndFromMap(state),
      (state) => selectRawApiRelationsToAndFromMap(state),
      (state) => selectEducationalActivityTypes(state),
      (state, href: ContentHref) => href,
      (state, href: ContentHref, config) => config,
    ],
    (relations, rawRelationsMap, educationalActivityTypes, href, config): string | null => {
      const newValues = getFoundationalSuggestions(
        educationalActivityTypes,
        relations,
        href,
        config,
      )?.filter((r) => r.isChecked);
      const oldValues = getFoundationalSuggestions(
        educationalActivityTypes,
        rawRelationsMap,
        href,
        config,
      )?.filter((r) => r.isChecked);

      const diffHtml = getDiffForArraysOfStrings(
        oldValues?.map((z) => z.name || ''),
        newValues?.map((z) => z.name || ''),
      );

      return diffHtml;
    },
  );
